import React, { memo, useEffect, useState } from "react";
import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  makeStyles,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  selectContainer: {
    marginTop: "20px",
  },
  gridItem: {
    display: "flex",
    flexDirection: "column",
  },
  truncatedInput: {
    width: "150px",
    height: "40px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
  },
  select: {
    paddingRight: "32px !important",
    "&:focus": {
      backgroundColor: "transparent !important",
    },
  },
}));

const STORAGE_KEY = "checkboxState";

const CheckboxGrid = ({ searchFiltersCheckboxes, handleCheckboxChange }) => {
  const classes = useStyles();
  const [selectedOptions, setSelectedOptions] = useState({});

  useEffect(() => {
    const savedState = sessionStorage.getItem(STORAGE_KEY);
    if (savedState) {
      setSelectedOptions(JSON.parse(savedState));
    }
  }, []);

  useEffect(() => {
    const selectedCheckboxes = Object.values(selectedOptions).flat();
    const selectedCheckboxesString = selectedCheckboxes.join(",");
    handleCheckboxChange(selectedCheckboxesString);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selectedOptions));
  }, [selectedOptions, handleCheckboxChange])

  const handleSelectChange = (category) => (event) => {
    const { value } = event.target;
  
    setSelectedOptions((prevSelectedOptions) => {
      const updatedOptions = {
        ...prevSelectedOptions,
        [category]: value,
      };
  
      // Update the selected values based on other categories
      Object.entries(updatedOptions).forEach(([key, selectedValues]) => {
        updatedOptions[key] = searchFiltersCheckboxes
          .filter((item) => updatedOptions[item.category]?.length > 0)
          .map((item) => item.value)
          .filter((val) => selectedValues.includes(val));
      });
  
      const selectedCheckboxes = Object.values(updatedOptions).flat();
      const selectedCheckboxesString = selectedCheckboxes.join(",");
      handleCheckboxChange(selectedCheckboxesString);
  
      return updatedOptions;
    });
  };

  const handleSelectAll = (category) => () => {
    const allValues = searchFiltersCheckboxes
      .filter((item) => item.category === category)
      .map((item) => item.value);
  
    setSelectedOptions((prevSelectedOptions) => {
      const isSelectedAll = !prevSelectedOptions[category] || prevSelectedOptions[category].length !== allValues.length;
  
      const updatedOptions = {
        ...prevSelectedOptions,
        [category]: isSelectedAll ? allValues : [],
      };
  
      return updatedOptions;
    });
  };
     
  const renderValue = (selected, category) => {
    if (selected?.length === 0) return "Select...";
  
    let displayedValue = selected.map((value) => {
      const matchingCheckbox = searchFiltersCheckboxes.find(
        (checkbox) => checkbox.value === value
      );
      return matchingCheckbox ? matchingCheckbox.text : value;
    });
  
    displayedValue = displayedValue.join(", ");
  
    return truncateText(displayedValue);
  };

  const truncateText = (text, maxLength = 20) =>
    text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

    return (
      <Grid container spacing={2} className={classes.selectContainer}>
        {Array.from(new Set(searchFiltersCheckboxes.map((item) => item.category))).map(
          (category) => (
            <Grid
              item
              key={category}
              xs={12}
              sm={6}
              md={4}
              lg={2}
              className={classes.gridItem}
            >
              <div>
                <h4>{category}</h4>
                <FormControl>
                  <Select
                    multiple
                    value={selectedOptions[category] || []}
                    onChange={handleSelectChange(category)}
                    renderValue={(selected) => renderValue(selected, category)}
                    displayEmpty={true}
                    inputProps={{
                      className: classes.truncatedInput,
                    }}
                    MenuProps={{
                      getContentAnchorEl: null,
                      MenuListProps: {
                        disablePadding: true,
                      },
                    }}
                    variant="outlined"
                    classes={{
                      select: classes.select,
                    }}
                  >
                    <MenuItem>
                      <Checkbox
                      id='selectAll'
                        checked={
                          selectedOptions[category]?.length ===
                          searchFiltersCheckboxes.filter((item) => item.category === category)
                            .length
                        }
                        onChange={handleSelectAll(category)}
                      />
                      <label htmlFor="selectAll" style={{cursor:'pointer', width: '100%'}}>Select All</label>
                    </MenuItem>
                    {searchFiltersCheckboxes
                      .filter((item) => item.category === category)
                      .map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          <Checkbox
                            checked={
                              selectedOptions[category]?.includes(item.value) || false
                            }
                          />
                          <ListItemText primary={item.text} />
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </div>
            </Grid>
          )
        )}
      </Grid>
    );
};

export default memo(CheckboxGrid);
